import { useChat } from '@ai-sdk/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DefaultChatTransport,
  isToolUIPart,
  lastAssistantMessageIsCompleteWithApprovalResponses,
  type UIMessage,
} from 'ai';
import { ArrowUp, Sparkles, SquarePen, Square } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useSearch } from 'wouter';
import { z } from 'zod';
import { DockedPane, PaneBack, PaneClose } from '../pane/DockedPane.js';
import { type Conversation, fetchCurrentConversation, startConversation } from './api.js';
import { currentFocus } from './focus.js';
import { MessageView } from './MessageView.js';
import './assistant.css';

const CONVERSATION_KEY = ['assistant', 'conversation'];

const SUGGESTIONS = [
  'Which tracked funds lead Series A in fintech, and who knows them?',
  'Who knows people at the fund open here?',
  'Which discovered funds co-invested with our portfolio most often?',
];

/**
 * The assistant, docked at the right of every page like the fund pane. It
 * opens the person's newest conversation (kept on the server, so a reload
 * shows it again); "New conversation" starts another.
 */
export function AssistantPanel(props: { onClose: () => void }): React.JSX.Element {
  const queryClient = useQueryClient();
  const conversation = useQuery({
    queryKey: CONVERSATION_KEY,
    queryFn: fetchCurrentConversation,
    staleTime: Infinity,
    retry: false,
  });
  const [starting, setStarting] = useState(false);

  const startNew = async (): Promise<void> => {
    setStarting(true);
    try {
      queryClient.setQueryData(CONVERSATION_KEY, await startConversation());
    } finally {
      setStarting(false);
    }
  };

  return (
    <DockedPane label="Assistant" onClose={props.onClose} className="assistant-pane">
      {conversation.data === undefined ? (
        <>
          <PanelHead onClose={props.onClose} onNew={null} cost={null} />
          <p className="subtle assistant-empty">
            {conversation.error === null ? '…' : conversation.error.message}
          </p>
        </>
      ) : (
        <Chat
          // A new conversation is a new chat: nothing of the old one carries over.
          key={conversation.data.id}
          conversation={conversation.data}
          onClose={props.onClose}
          onNew={starting ? null : () => void startNew()}
        />
      )}
    </DockedPane>
  );
}

const metadataSchema = z.object({ conversationCostUsd: z.number() });

function Chat(props: {
  conversation: Conversation & { maxUsd: number };
  onClose: () => void;
  onNew: (() => void) | null;
}): React.JSX.Element {
  const { conversation } = props;
  const [location] = useLocation();
  const search = useSearch();
  const where = useRef({ location, search });
  where.current = { location, search };

  const transport = useMemo(
    () =>
      new DefaultChatTransport({
        api: '/assistant/chat',
        credentials: 'same-origin',
        // The server holds the conversation: it is sent the new question, or
        // the decisions on the cards, and where the person is.
        prepareSendMessagesRequest: ({ messages }) => {
          const last = messages.at(-1);
          const context = pageContext(where.current.location, where.current.search);
          if (last?.role === 'assistant') {
            return {
              body: { conversationId: conversation.id, decisions: decisionsOf(last), context },
            };
          }
          const words = (last?.parts ?? []).flatMap((part) =>
            part.type === 'text' ? [part.text] : [],
          );
          return {
            body: {
              conversationId: conversation.id,
              message: {
                id: last?.id,
                role: 'user',
                parts: [{ type: 'text', text: words.join('\n') }],
              },
              context,
            },
          };
        },
      }),
    [conversation.id],
  );

  const chat = useChat({
    id: conversation.id,
    messages: conversation.messages,
    transport,
    sendAutomaticallyWhen: lastAssistantMessageIsCompleteWithApprovalResponses,
  });

  const busy = chat.status === 'submitted' || chat.status === 'streaming';
  const cost = useMemo(() => {
    for (const message of [...chat.messages].reverse()) {
      const parsed = metadataSchema.safeParse(message.metadata);
      if (parsed.success) return parsed.data.conversationCostUsd;
    }
    return conversation.costUsd;
  }, [chat.messages, conversation.costUsd]);
  const spent = cost >= conversation.maxUsd;

  const scroller = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const element = scroller.current;
    if (element !== null) element.scrollTop = element.scrollHeight;
  }, [chat.messages]);

  const [draft, setDraft] = useState('');
  const send = (text: string): void => {
    const question = text.trim();
    if (question === '' || busy || spent) return;
    setDraft('');
    void chat.sendMessage({ text: question });
  };

  return (
    <div className="assistant-chat">
      <PanelHead onClose={props.onClose} onNew={props.onNew} cost={cost} />

      <div className="assistant-scroll" ref={scroller}>
        {chat.messages.length === 0 ? (
          <div className="assistant-welcome">
            <span className="assistant-welcome-icon" aria-hidden="true">
              <Sparkles />
            </span>
            <p>Ask about funds, people, rounds or your lists. Changes wait for your click.</p>
            <div className="assistant-suggestions">
              {SUGGESTIONS.map((suggestion) => (
                <button
                  key={suggestion}
                  type="button"
                  className="assistant-suggestion"
                  onClick={() => send(suggestion)}
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        ) : (
          chat.messages.map((message) => (
            <MessageView
              key={message.id}
              message={message}
              busy={busy}
              onDecide={(approvalId, approved) =>
                void chat.addToolApprovalResponse({ id: approvalId, approved })
              }
            />
          ))
        )}
        {chat.status === 'submitted' && <p className="subtle assistant-thinking">Thinking…</p>}
        {chat.error !== undefined && (
          <p className="assistant-error" role="alert">
            {readableError(chat.error)}
          </p>
        )}
        {spent && (
          <p className="assistant-error">
            This conversation has used its ${conversation.maxUsd.toFixed(2)}. Start a new one to
            carry on.
          </p>
        )}
      </div>

      <form
        className="assistant-composer"
        onSubmit={(event) => {
          event.preventDefault();
          send(draft);
        }}
      >
        <textarea
          value={draft}
          rows={2}
          placeholder="Ask about the list…"
          aria-label="Your question"
          disabled={spent}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
              event.preventDefault();
              send(draft);
            }
          }}
        />
        {busy ? (
          <button
            type="button"
            className="icon-button assistant-send"
            aria-label="Stop"
            title="Stop"
            onClick={() => void chat.stop()}
          >
            <Square aria-hidden="true" />
          </button>
        ) : (
          <button
            type="submit"
            className="icon-button assistant-send"
            aria-label="Send"
            title="Send (Enter)"
            disabled={draft.trim() === '' || spent}
          >
            <ArrowUp aria-hidden="true" />
          </button>
        )}
      </form>
    </div>
  );
}

function PanelHead(props: {
  onClose: () => void;
  onNew: (() => void) | null;
  cost: number | null;
}): React.JSX.Element {
  return (
    <header className="pane-head assistant-head">
      <PaneBack onClose={props.onClose} />
      <div className="assistant-title">
        <span className="assistant-title-icon" aria-hidden="true">
          <Sparkles />
        </span>
        <strong>Assistant</strong>
        {props.cost !== null && props.cost > 0 && (
          <span className="assistant-cost" title="What this conversation has cost so far">
            ${props.cost < 0.01 ? props.cost.toFixed(4) : props.cost.toFixed(2)}
          </span>
        )}
      </div>
      <button
        type="button"
        className="icon-button"
        aria-label="New conversation"
        title="New conversation"
        disabled={props.onNew === null}
        onClick={() => props.onNew?.()}
      >
        <SquarePen aria-hidden="true" />
      </button>
      <PaneClose onClose={props.onClose} />
    </header>
  );
}

/** The cards on the last answer the person has now decided. */
function decisionsOf(message: UIMessage): { approvalId: string; approved: boolean }[] {
  return message.parts.flatMap((part) =>
    isToolUIPart(part) && part.state === 'approval-responded'
      ? [{ approvalId: part.approval.id, approved: part.approval.approved }]
      : [],
  );
}

/** Where the person is: the page, and what is open on it. */
function pageContext(
  location: string,
  search: string,
): { path: string; fundId?: string; listId?: string; companyId?: string } {
  const params = new URLSearchParams(search);
  const focus = currentFocus();
  const listId = /^\/lists\/([0-9a-f-]{36})/.exec(location)?.[1];
  const fundId = focus.fundId ?? params.get('fund') ?? undefined;
  const companyId = focus.companyId ?? params.get('company') ?? undefined;
  return {
    path: location,
    ...(fundId === undefined ? {} : { fundId }),
    ...(listId === undefined ? {} : { listId }),
    ...(companyId === undefined ? {} : { companyId }),
  };
}

/** The transport reports a refusal as its body; the server's sentence is inside. */
function readableError(error: Error): string {
  try {
    const parsed = z.object({ error: z.string() }).safeParse(JSON.parse(error.message));
    if (parsed.success) return parsed.data.error;
  } catch {
    // Not the server's JSON: say what the error says.
  }
  return error.message;
}
